import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom-v5-compat';
import { Button, Grid, Typography } from '@mui/material';
import {
    DrawerBody,
    DrawerHeading,
    DrawerFooter,
    TagMultiselect,
    UncontrolledGenericDrawer
} from '@/components/common';
import { useDrawers, useUrlParams, useToastNotification } from '@/hooks';
import {
    PRODUCT_TYPE_PRODUCT,
    PRODUCT_TYPE_SERVICE,
    CLIENT_BASE_PARAM,
    PRODUCT_BASE_PARAM,
    SERVICE_BASE_PARAM,
    TAX_BASE_PARAM,
    DISCOUNT_BASE_PARAM,
    ESTIMATE_BASE_PARAM,
    RECEIPT_BASE_PARAM,
    INVOICE_BASE_PARAM
} from '@/constants';
import {
    updateProduct,
    getPaginatedProducts,
    updateTax,
    getPaginatedTaxes,
    updateDiscount,
    getPaginatedDiscounts,
    updateClient,
    getPaginatedClients,
    updateInvoice,
    getInvoices,
    updateEstimate,
    getAllEstimates,
    updateReceipt,
    getReceipts,
    updateLockedDocumentTags
} from '@/modules/dataWrangler/dataWrangler';
import { titleCase, getTagIds } from '@/util';
import { AddIcon, TagsIconBlue, SaveIcon } from '@/resources/icons';
import { closeDrawer, openDrawer, setDocumentTags } from '@/state/actions';
import useStyles from './styles';

const TagsDrawer = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { getDrawer } = useDrawers();
    const { triggerToast } = useToastNotification();
    const [saving, setSaving] = useState(false);

    const params = useParams();
    const { getUrlParams } = useUrlParams();

    const currentProdUrlParams = getUrlParams({ base: PRODUCT_BASE_PARAM });
    const currentServiceUrlParams = getUrlParams({ base: SERVICE_BASE_PARAM });
    const currentTaxUrlParams = getUrlParams({ base: TAX_BASE_PARAM });
    const currentDiscountUrlParams = getUrlParams({
        base: DISCOUNT_BASE_PARAM
    });
    const currentClientUrlParams = getUrlParams({ base: CLIENT_BASE_PARAM });
    const currentInvoiceUrlParams = getUrlParams({ base: INVOICE_BASE_PARAM });
    const currentReceiptUrlParams = getUrlParams({ base: RECEIPT_BASE_PARAM });
    const currentEstimateUrlParams = getUrlParams({
        base: ESTIMATE_BASE_PARAM
    });

    const tags = useSelector((state) => state.user?.activeBusiness?.tags) || [];

    const tagsDrawer = getDrawer('tags');
    const drawerOpen = Boolean(tagsDrawer);
    const type = tagsDrawer?.type;
    const title = tagsDrawer?.type ? `${titleCase(type)} Tags` : 'Tags';
    const item = tagsDrawer?.item;
    const updatedTags = tagsDrawer?.updatedTags;
    const makeDocumentPage = tagsDrawer?.makeDocument;
    const isDraft = item?.documentState === 'draft';
    const isDocument = item?.documentState;
    const toastName = isDocument
        ? item?.customLabel || item?.referenceNumber
        : item?.name;

    const methods = useForm({
        mode: 'onSubmit',
        values: {
            tags: updatedTags || item?.tags || []
        }
    });
    const { handleSubmit, reset, getValues } = methods;

    const handleOpenCreateTags = () => {
        dispatch(
            openDrawer({
                openedFromDrawer: 'tags',
                id: 'createTags',
                type,
                item,
                makeDocumentPage,
                currentTags: getValues()?.tags
            })
        );
    };

    const handleCloseDrawer = () => {
        dispatch(closeDrawer('tags'));
        reset();
    };

    const submitForm = async (data) => {
        setSaving(true);

        const documentTags = data.tags;
        const tagIds = getTagIds(data.tags);

        let currentUrlParams = {};

        if (type === 'product' || type === 'service') {
            await updateProduct({
                productId: item.productId,
                name: item.name,
                price: Number(item.price),
                type:
                    type === 'product'
                        ? PRODUCT_TYPE_PRODUCT
                        : PRODUCT_TYPE_SERVICE,
                description: item.description,
                quantity: parseFloat(item.quantity),
                tagIds
            });

            getPaginatedProducts({
                args:
                    type === 'product'
                        ? currentProdUrlParams
                        : currentServiceUrlParams,
                type
            });

            currentUrlParams =
                type === 'product'
                    ? currentProdUrlParams
                    : currentServiceUrlParams;
        } else if (type === 'tax') {
            await updateTax({
                taxId: item.taxId,
                name: item.name,
                value: parseFloat(item.value),
                type: 'percent',
                tagIds
            });

            getPaginatedTaxes(currentTaxUrlParams);

            currentUrlParams = currentTaxUrlParams;
        } else if (type === 'discount') {
            await updateDiscount({
                discountId: item.discountId,
                name: item.name,
                value: parseFloat(item.value),
                type: item.type,
                tagIds
            });

            getPaginatedDiscounts(currentDiscountUrlParams);

            currentUrlParams = currentDiscountUrlParams;
        } else if (type === 'client') {
            await updateClient({ ...item, tagIds });

            getPaginatedClients(currentClientUrlParams);

            currentUrlParams = currentClientUrlParams;
        } else if (type === 'invoice') {
            currentUrlParams = currentInvoiceUrlParams;
        } else if (type === 'estimate') {
            currentUrlParams = currentEstimateUrlParams;
        } else if (type === 'receipt') {
            currentUrlParams = currentReceiptUrlParams;
        }

        if (isDocument && !isDraft) {
            await updateLockedDocumentTags(item.documentId, type, documentTags);
        }

        if (isDocument && isDraft && makeDocumentPage) {
            dispatch(setDocumentTags(documentTags));
        }

        if (isDocument && !makeDocumentPage) {
            if (params?.clientId) {
                currentUrlParams.client = params.clientId;
                currentUrlParams.prefilters = 'clientId';
            }

            if (type === 'invoice') {
                if (isDraft) {
                    await updateInvoice({ ...item, tagIds });
                }

                getInvoices({ ...currentUrlParams });
            }

            if (type === 'estimate') {
                if (isDraft) {
                    await updateEstimate({ ...item, tagIds });
                }

                getAllEstimates({ ...currentUrlParams });
            }

            if (type === 'receipt') {
                if (isDraft) {
                    await updateReceipt({ ...item, tagIds });
                }

                getReceipts({ ...currentUrlParams });
            }
        }

        setSaving(false);
        handleCloseDrawer();

        if (!isDraft) {
            triggerToast({
                message: `Updated "${toastName}" tags`,
                action: 'saved',
                icon: <SaveIcon />
            });
        }
    };

    return (
        <UncontrolledGenericDrawer
            open={drawerOpen}
            close={handleCloseDrawer}
            id="tags"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(submitForm)}
                    className={classes.tagDrawerContainer}
                >
                    <DrawerHeading
                        startAdornment={<TagsIconBlue />}
                        title={title}
                    />
                    <DrawerBody
                        className={`${classes.tagDrawerBody} ${
                            !tags.length ? classes.centerDrawerBody : ''
                        }`}
                    >
                        {tags.length ? (
                            <Grid
                                item
                                xs={12}
                                justifyContent="flex-start"
                                className={classes.hasTagsContainer}
                            >
                                <Grid
                                    item
                                    container
                                    direction="column"
                                    rowSpacing={4}
                                >
                                    <Grid item>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<AddIcon />}
                                            classes={{
                                                root: classes.createTagButton,
                                                startIcon:
                                                    classes.createTagButtonIcon
                                            }}
                                            onClick={handleOpenCreateTags}
                                        >
                                            Create New Tag
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <TagMultiselect />
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid
                                item
                                xs={12}
                                container
                                justifyContent="center"
                                alignItems="center"
                                rowSpacing={3}
                            >
                                <Grid item>
                                    <Typography
                                        variant="h1"
                                        component="h2"
                                        align="center"
                                    >
                                        You don&apos;t have any tags created.
                                        <br />
                                        Setup your first tag
                                    </Typography>
                                </Grid>

                                <Grid item xs={8}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        className={classes.createTagButton}
                                        onClick={handleOpenCreateTags}
                                    >
                                        Create New Tag
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </DrawerBody>
                    <DrawerFooter
                        submitButtonId="tag-drawer-submit"
                        acceptOptions={{
                            label: 'Save',
                            type: 'submit'
                        }}
                        cancelOptions={{
                            label: 'Cancel',
                            action: handleCloseDrawer
                        }}
                        saving={saving}
                    />
                </form>
            </FormProvider>
        </UncontrolledGenericDrawer>
    );
};

export default TagsDrawer;
